/**
 * Shared TypeScript interfaces and types for all API modules.
 * Extracted from the original monolithic api.ts for reuse across domain modules.
 */

export interface ConnectorConfig {
  connector_id: string;
  connector_type: string;
  name: string;
  direction: 'source' | 'destination' | 'bidirectional';
  config: Record<string, any>;
  project_id?: string;
  tenant_id?: string;
  status: string;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
}

export interface ExportToConnectorResponse {
  batch_id: string;
  connector_id: string;
  files_exported: number;
  total_rows: number;
  exported_files: Array<{ table: string; path: string; rows: number; checksum?: string }>;
  status: string;
}

export interface ExportJobAcceptedResponse {
  job_id: string;
  batch_id: string;
  status: string;
}

export interface Batch {
  batch_id: string;
  batch_name?: string;
  client_id?: string;
  files?: string[];
  status:
  | 'uploaded'
  | 'pending'
  | 'profiling'
  | 'profiled'
  | 'summarizing'
  | 'summarized'
  | 'ready_for_mapping'
  | 'researching'
  | 'staged'
  | 'mapping'
  | 'mapped'
  | 'reviewing'
  | 'approved'
  | 'transforming'
  | 'loading'
  | 'completed'
  | 'failed';
  created_at?: string;
  updated_at?: string;
  started_at?: string;
  completed_at?: string;
  total_files?: number;
  total_rows?: number;
  processed_rows?: number;
  failed_rows?: number;
  error_message?: string | null;
  file_path?: string;
  file_type?: string;
  project_id?: string | null;
  target_schema_id?: string | null;
  schema_version_hash?: string | null;
  // Agent staging output fields (populated when status === 'staged')
  staging_output_name?: string;
  staging_row_count?: number;
  staging_col_count?: number;
  staging_step_count?: number;
  staging_changes_summary?: string;
  mapping_count?: number;
}

export interface Schema {
  schema_id: string;
  tenant_id?: string | null;
  schema_name: string;
  version: number;
  status: string;
  definition: Record<string, any>;
  metadata?: Record<string, any>;
  validator_config?: Record<string, any>;
  template_key?: string | null;
  base_schema_id?: string | null;
  effective_at?: string | null;
  project_id?: string | null;
  source_type?: string | null;
  provenance?: Record<string, any>;
  approved_by?: string | null;
  approved_at?: string | null;
  created_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface SchemaListParams {
  tenantId?: string | null;
  status?: string | null;
  name?: string | null;
  projectId?: string | null;
  sourceType?: string | null;
  limit?: number;
}

export interface SchemaCreateRequest {
  tenantId?: string | null;
  schemaName: string;
  definition?: Record<string, any>;
  metadata?: Record<string, any>;
  validatorConfig?: Record<string, any>;
  projectId?: string | null;
  sourceType?: string | null;
  provenance?: Record<string, any>;
  status?: string;
  createdBy?: string | null;
}

export interface SchemaUpdateRequest {
  schemaName?: string | null;
  status?: string | null;
  definition?: Record<string, any>;
  metadata?: Record<string, any>;
  validatorConfig?: Record<string, any>;
  provenance?: Record<string, any>;
  approvedBy?: string | null;
  approvedAt?: string | null;
}

export interface SchemaCloneRequest {
  schemaName?: string;
  status?: string;
  createdBy?: string | null;
}

export interface SchemaPromoteRequest {
  status?: string;
  approvedBy: string;
  metadata?: Record<string, any>;
  schemaName?: string;
}

export interface SchemaGenerateRequest {
  tenantId?: string | null;
  projectId?: string | null;
  provider?: string | null;
  sourceType: string;
  payload: Record<string, any>;
  createdBy: string;
  metadata?: Record<string, any>;
}

export interface SchemaGenerateResponse {
  status: string;
  provider?: string | null;
  resolved_provider?: string | null;
  prompt_version: string;
  prompt_hash?: string | null;
  issues: string[];
  schema: Schema;
}


export interface SchemaUploadRequest {
  sourceType: 'file_upload' | 'sql_ddl' | 'json_template' | 'csv_template' | 'excel_template' | 'template';
  content: string;
  additionalFiles?: Record<string, string> | null;
  schemaName?: string | null;
  schemaSetId?: string | null;
  tenantId?: string | null;
  metadata?: Record<string, any>;
  createdBy?: string | null;
  selectedSheets?: string[] | null;
  selectedColumnsBySheet?: Record<string, string[]> | null;
}

export interface SchemaUploadResponse {
  schema: Schema;
  parsed_tables: number;
  parsed_columns: number;
  issues: string[];
  /** Present when sourceType='template'. Structural summary for preview. */
  template_summary?: {
    format: 'xml' | 'json';
    depth: number;
    element_count: number;
    unique_elements: number;
    structure?: Array<{
      name: string;
      path: string;
      count: number;
      depth: number;
      children: string[];
    }>;
    xsd_metadata?: {
      target_namespace?: string;
      groups?: Array<{ name: string; elements?: any[]; group_refs?: string[] }>;
      attribute_groups?: Array<{ name: string; attributes?: any[] }>;
      resolution?: {
        resolved_imports: string[];
        unresolved_imports: string[];
        resolved_type_count: number;
        resolved_enum_count: number;
        issues: string[];
      };
      [key: string]: any;
    } | null;
  } | null;
}

export interface ConnectorSchemaExtractRequest {
  connectorId?: string | null;
  connectorType?: string | null;
  config?: Record<string, any>;
  tableNames?: string[] | null;
  schemaName?: string | null;
  schemaSetId?: string | null;
  tenantId?: string | null;
  metadata?: Record<string, any>;
  createdBy?: string | null;
}

export interface ConnectorSchemaExtractResponse {
  schema: Schema;
  parsed_tables: number;
  parsed_columns: number;
  extraction_method: string;
  available_tables: string[];
  issues: string[];
}

export interface Project {
  project_id: string;
  tenant_id?: string | null;
  name: string;
  description?: string | null;
  status: string;
  target_schema_id?: string | null;
  source_connector_id?: string | null;
  destination_connector_id?: string | null;
  metadata?: Record<string, any>;
  created_by?: string | null;
  is_deleted?: boolean;
  deleted_at?: string | null;
  deleted_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface BatchSummary {
  batch_id: string;
  batch_name?: string;
  client_id: string;
  status: string;
  project_id?: string | null;
  target_schema_id?: string | null;
  schema_version_hash?: string | null;
  created_at?: string | null;
  total_rows?: number | null;
  processed_rows?: number | null;
  failed_rows?: number | null;
  // Schedule lineage (populated when batch has an approved recipe used by a schedule)
  active_recipe_id?: string | null;
  schedule_name?: string | null;
  total_scheduled_runs?: number | null;
  last_run_status?: string | null;
  last_run_at?: string | null;
}

export interface ProjectSummary {
  project: Project;
  target_schema?: Schema;
  schema?: Schema;
  batches: BatchSummary[];
  pending_schemas: Schema[];
  recent_schemas: Schema[];
}

/** Resolves embedded target schema from project summary (handles `target_schema` and legacy `schema`). */
export function resolveProjectSummaryTargetSchema(
  summary: ProjectSummary | undefined | null,
): Schema | undefined {
  return summary?.target_schema ;
}

export interface ProjectSummaryListResponse {
  projects: ProjectSummary[];
}

export interface ProjectCreateRequest {
  tenantId?: string;
  name: string;
  description?: string | null;
  status?: string;
  targetSchemaId?: string | null;
  sourceConnectorId?: string | null;
  destinationConnectorId?: string | null;
  metadata?: Record<string, any>;
  createdBy?: string | null;
}

export interface ProjectUpdateRequest {
  name?: string;
  description?: string | null;
  status?: string | null;
  targetSchemaId?: string | null;
  sourceConnectorId?: string | null;
  destinationConnectorId?: string | null;
  metadata?: Record<string, any>;
}

export interface ProjectListParams {
  tenantId?: string | null;
  status?: string | null;
  search?: string | null;
  includeDeleted?: boolean;
  limit?: number;
}

export interface Profile {
  batch_id: string;
  file_name: string;
  columns: ColumnProfile[];
  row_count: number;
  file_size_mb: number;
  source_uri?: string;
  file_format?: string;
  profiled_at?: string;
}

export interface ColumnProfile {
  column_name: string;
  data_type: string;
  null_percentage: number;
  unique_count: number;
  sample_values: string[];
  min_value?: string | number;
  max_value?: string | number;
  avg_length?: number;
  histogram?: { [key: string]: number };
  pii_detected?: boolean;
  confidence_score?: number;
}

export interface TransformationPlanStep {
  step_id: string;
  operation: string;
  description: string;
  inputs: string[];
  outputs: string[];
  parameters: Record<string, unknown>;
  confidence: number;
  ai_generated: boolean;
  engine?: 'pandas' | 'sql' | 'beam' | 'python';
  metadata?: Record<string, unknown>;
}

export interface TransformationPlanTarget {
  target_table: string;
  description: string;
  steps: TransformationPlanStep[];
}

export type TransformationPlanHintScope = 'plan' | 'target' | 'step' | 'join';
export type TransformationPlanHintCategory =
  | 'schema'
  | 'expression'
  | 'join'
  | 'preview'
  | 'runtime'
  | 'quality';
export type TransformationPlanHintSource = 'semantic' | 'execution' | 'validator' | 'review';
export type TransformationPlanHintSeverity = 'info' | 'warning' | 'error';

export interface TransformationPlanHint {
  hint_id: string;
  scope: TransformationPlanHintScope;
  category: TransformationPlanHintCategory;
  message: string;
  severity: TransformationPlanHintSeverity;
  remediation?: string | null;
  source: TransformationPlanHintSource;
  step_id?: string | null;
  target_table?: string | null;
  join_id?: string | null;
  metadata?: Record<string, any>;
}

export interface TransformationJoinCondition {
  left: string;
  right: string;
  operator?: '=' | '!=' | '>' | '<' | '>=' | '<=' | 'in' | 'like';
}

export interface TransformationJoinInput {
  source_alias: string;
  join_keys?: string[];
  derived_from?: string | null;
  filter?: string | null;
}

export interface TransformationJoinNode {
  join_id: string;
  join_type: 'inner' | 'left' | 'right' | 'full' | 'cross';
  description?: string;
  inputs: TransformationJoinInput[];
  conditions: TransformationJoinCondition[];
  output_alias: string;
  materialized?: boolean;
  metadata?: Record<string, any>;
}

export interface TransformationJoinTree {
  version: string;
  root?: string | null;
  nodes: Record<string, TransformationJoinNode>;
}

export interface SchemaColumnSnapshot {
  name: string;
  data_type?: string;
  type?: string;
  display_name?: string;
  is_nullable?: boolean;
  nullable?: boolean;
  is_primary_key?: boolean;
  primary_key?: boolean;
  column_name?: string;
  [key: string]: unknown;
}

export interface SchemaTableSnapshot {
  display_name?: string;
  name?: string;
  columns?: SchemaColumnSnapshot[] | Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export interface SchemaSetSnapshot {
  name?: string;
  status?: string;
  tables?: Record<string, SchemaTableSnapshot>;
  sheets?: CanvasSheetDescriptorModel[];
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface SkippedTable {
  target_table: string;
  reason: string;
}

export interface RejectedRelationship {
  from: string;
  to: string;
  score: number;
  reason: string;
}

export interface TransformationPlan {
  version: string;
  metadata: Record<string, unknown> & {
    rejected_relationships?: RejectedRelationship[];
  };
  dependent_sources: string[];
  targets: TransformationPlanTarget[];
  join_tree?: TransformationJoinTree | null;
  hints?: TransformationPlanHint[];
  target_schema_snapshot?: SchemaSetSnapshot | null;
  skipped_tables?: SkippedTable[];
}

export interface Suggestion {
  suggestion_id: string;
  title: string;
  description: string;
  kind: string;
  confidence: number;
  ai_generated: boolean;
  decision?: string;
  decided_by?: string;
  decided_at?: string;
  reason?: string;
}

export interface Observation {
  observation_id: string;
  title: string;
  detail: string;
  severity: string;
  source?: string;
  ai_generated: boolean;
}

export interface ValidatorRun {
  run_id: string;
  batch_id: string;
  recipe_id?: string;
  stage: string;
  status: string;
  provider?: string;
  prompt_hash?: string;
  validator_config: Record<string, any>;
  metrics: Record<string, any>;
  issues: string[];
  metadata: Record<string, any>;
  observed_at: string;
}

export interface ValidatorRunListResponse {
  batch_id: string;
  recipe_id?: string;
  runs: ValidatorRun[];
}

export interface SchemaDriftEvent {
  event_id: string;
  batch_id: string;
  client_id?: string;
  schema_name?: string;
  schema_version?: number;
  severity: string;
  issues: Record<string, any>;
  observed_signature: Record<string, any>;
  expected_signature?: Record<string, any> | null;
  metadata: Record<string, any>;
  detected_at: string;
}

export interface SchemaDriftEventsResponse {
  batch_id: string;
  client_id?: string;
  summary: Record<string, any>;
  events: SchemaDriftEvent[];
}

export interface HealthIssue {
  category: string;
  severity: 'error' | 'warning' | 'info';
  target_table: string;
  target_field?: string;
  rule_type?: string;
  message: string;
  violation_count?: number;
  total_rows?: number;
  sample_violations?: any[];
  row_indices?: number[];
  count?: number;
  remediation?: string;
  fix_actions?: Array<{ label: string; fix_type: string }>;
}

export interface ExpressionAnalysis {
  has_cast: boolean;
  cast_target_type?: string;
  has_coalesce: boolean;
  coalesce_default?: string;
  output_type_hint?: string;
  referenced_columns: string[];
  is_aggregate: boolean;
  is_window: boolean;
}

export interface ExpressionValidationResponse {
  valid: boolean;
  error?: string;
  columns?: string[];
  analysis?: ExpressionAnalysis;
}

export interface MappingHealthSummary {
  total_mappings: number;
  expression_errors: number;
  schema_rule_violations: number;
  unmapped_targets: number;
  low_confidence_count: number;
}

export interface MappingHealthResponse {
  batch_id: string;
  recipe_id?: string;
  overall_status: 'healthy' | 'warnings' | 'errors';
  summary: MappingHealthSummary;
  issues: HealthIssue[];
}

export interface TransformationRule {
  rule_id: string;
  name: string;
  category?: string;
  description?: string;
  expression: string;
  example: Record<string, any>;
  tags: string[];
  created_by?: string;
  created_at?: string;
  updated_at?: string;
}

export interface TransformationRuleListResponse {
  rules: TransformationRule[];
}

export type CanvasStatus = 'draft' | 'pending_review' | 'approved' | 'rejected' | 'deprecated';
export type CanvasNodeKind =
  | 'sourceField'
  | 'targetField'
  | 'transformation'
  | 'aggregation'
  | 'intermediateField'
  | 'linkBridge'
  | 'validation'
  | 'tableGroup'
  | 'lookup';
export type CanvasEdgeKind = 'dataFlow' | 'aggregation' | 'validation' | 'lookup';
export type CanvasProvenanceOrigin = 'llm' | 'manual' | 'hybrid';

export interface CanvasPositionModel {
  x: number;
  y: number;
  width?: number;
  height?: number;
}

export interface CanvasNodeUIStateModel {
  position?: CanvasPositionModel;
  collapsed?: boolean;
  badges?: Record<string, string>;
  selected?: boolean;
}

export interface CanvasEdgeUIStateModel {
  highlighted?: boolean;
  badges?: Record<string, string>;
}

export interface CanvasNodeProvenanceModel {
  origin: CanvasProvenanceOrigin;
  llm_run_id?: string;
  prompt_template?: string;
  prompt_parameters?: Record<string, string>;
  model_id?: string;
  last_modified_by?: string;
  last_modified_at?: string;
}

export interface CanvasEdgeProvenanceModel {
  origin: CanvasProvenanceOrigin;
  llm_run_id?: string;
  prompt_template?: string;
  prompt_parameters?: Record<string, string>;
  model_id?: string;
  last_modified_by?: string;
  last_modified_at?: string;
}

export interface CanvasAggregationInputRangeModel {
  sheet_id: string;
  columns: string[];
  filters: Record<string, string>;
}

export interface CanvasAggregationNodeDataModel {
  operation: string;
  input_ranges: CanvasAggregationInputRangeModel[];
  group_by: string[];
  output_fields: string[];
  cache_key?: string;
  sampling_strategy?: string;
  validator_checks: string[];
}

export interface CanvasNodeModel {
  id: string;
  kind: CanvasNodeKind;
  label: string;
  sheet_id?: string;
  data: Record<string, string>;
  aggregation?: CanvasAggregationNodeDataModel;
  confidence?: number;
  status: CanvasStatus;
  provenance: CanvasNodeProvenanceModel;
  ui_state: CanvasNodeUIStateModel;
}

export interface CanvasEdgeModel {
  id: string;
  source: string;
  target: string;
  kind: CanvasEdgeKind;
  mapping_id?: string;
  transformation?: string;
  prompt_override?: string;
  prompt_parameters: Record<string, string>;
  confidence?: number;
  status: CanvasStatus;
  provenance: CanvasEdgeProvenanceModel;
  ui_state?: CanvasEdgeUIStateModel;
  style?: Record<string, any>;
}

export interface CanvasSheetDescriptorModel {
  sheet_id: string;
  display_name: string;
  source_type: string;
  sampling_profile?: string;
  row_count?: number;
  column_count?: number;
}

export interface CanvasWorkbookMetadataModel {
  workbook_id: string;
  name?: string;
  description?: string;
  sheets: CanvasSheetDescriptorModel[];
  dependency_dag: Record<string, string[]>;
}

export interface CanvasManualAdjustmentModel {
  adjustment_id: string;
  target_id: string;
  target_kind: 'node' | 'edge' | 'schema_table' | 'schema_field';
  author: string;
  rationale?: string | null;
  created_at: string;
  superseded_step_id?: string | null;
  reversible: boolean;
  status: CanvasStatus;
  ai_generated: boolean;
  annotations?: Record<string, any>;
  details?: Record<string, any>;
}

export interface ManualAdjustmentActionRequest {
  actor: string;
}

export interface EdgeRegenerateRequest {
  actor: string;
  instructions: string;
}

export interface CanvasGraphDiffModel {
  nodes_added: string[];
  nodes_removed: string[];
  nodes_updated: string[];
  edges_added: string[];
  edges_removed: string[];
  edges_updated: string[];
  metadata_changed: boolean;
}

export interface EdgeRegenerateResponseModel {
  graph: CanvasGraphModel;
  diff: CanvasGraphDiffModel;
  prompt_run: CanvasPromptRunRecordModel;
  edge_id: string;
}

export interface PromptRegenerateMappingRequest {
  recipeId: string;
  instructions: string;
  explanation?: string;
  actor?: string;
  provider?: string | null;
}

export interface SchemaEnrichmentRecordModel {
  values: Record<string, any>;
  updated_at: string;
  updated_by?: string;
}

export interface SchemaEnrichmentSnapshotModel {
  tables: Record<string, SchemaEnrichmentRecordModel>;
  fields: Record<string, SchemaEnrichmentRecordModel>;
  version?: number;
}

export interface CanvasPromptRunRecordModel {
  llm_run_id: string;
  prompt_template: string;
  prompt_parameters: Record<string, string>;
  model_id?: string;
  prompt_text?: string;
  response_text?: string;
  generated_at: string;
  regenerated_nodes: string[];
  regenerated_edges: string[];
  all_prompts?: Array<Record<string, any>>;
  token_usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
  cost_details?: {
    input_cost: number;
    output_cost: number;
    total_cost: number;
  };
}

export interface PromptLogEntry {
  log_id: string;
  batch_id: string;
  llm_run_id?: string;
  log_type: string;
  prompt_template?: string;
  model_id?: string;
  prompt_text?: string;
  response_text?: string;
  token_usage?: Record<string, any>;
  metadata?: Record<string, any>;
  created_at: string;
}

export interface RegenerationValidatorHint {
  hint_id: string;
  message: string;
  severity?: string;
  scope?: string;
  category?: string;
  remediation?: string | null;
  source?: string | null;
  step_id?: string | null;
  target_table?: string | null;
  join_id?: string | null;
}

export interface RegenerationContextResponse {
  batch_id: string;
  recipe_id: string;
  mapping_id: string;
  validator_hints: RegenerationValidatorHint[];
}

export interface CanvasAggregationExecutionPlanModel {
  aggregation_id: string;
  order: number;
  sheet_id?: string;
  inputs: string[];
  outputs: string[];
  cache_key?: string;
}

export type CanvasAggregationValidatorStatus = 'pending' | 'validated' | 'failed';

export interface CanvasAggregationCacheEntryModel {
  aggregation_id: string;
  cache_key: string;
  fingerprint: string;
  validator_status: CanvasAggregationValidatorStatus;
  last_computed_at?: string | null;
  last_validated_at?: string | null;
  validator_message?: string | null;
}

export interface CanvasGraphLockInfoModel {
  locked: boolean;
  locked_by?: string;
  locked_at?: string;
}

export interface CanvasGraphMetadataModel {
  version: string;
  revision: number;
  created_at: string;
  updated_at: string;
  workbook: CanvasWorkbookMetadataModel;
  lock: CanvasGraphLockInfoModel;
  manual_adjustments: CanvasManualAdjustmentModel[];
  prompt_runs: CanvasPromptRunRecordModel[];
  aggregation_plan: CanvasAggregationExecutionPlanModel[];
  aggregation_cache?: Record<string, CanvasAggregationCacheEntryModel>;
  schema_enrichment?: SchemaEnrichmentSnapshotModel;
  project_id?: string | null;
  target_schema_id?: string | null;
  schema_version_hash?: string | null;
  schema_set_snapshot?: Record<string, any> | null;
  join_tree?: Record<string, any> | null;
}

export interface CanvasGraphModel {
  graph_id: string;
  workbook_id: string;
  nodes: CanvasNodeModel[];
  edges: CanvasEdgeModel[];
  metadata: CanvasGraphMetadataModel;
}

export interface CanvasGraphResponse {
  graph: CanvasGraphModel;
}

export interface CanvasEventModel {
  event_id: string;
  workbook_id: string;
  graph_id: string;
  revision: number;
  actor: string;
  kind: string;
  payload: Record<string, string>;
  created_at: string;
}

export interface CanvasEventsResponse {
  events: CanvasEventModel[];
}

export interface RecordCanvasEventRequest {
  kind: string;
  actor: string;
  payload?: Record<string, string>;
  revision?: number;
}

export interface CreateManualAdjustmentRequest {
  target_id: string;
  target_kind: 'node' | 'edge' | 'schema_table' | 'schema_field';
  author: string;
  rationale?: string;
  status?: CanvasStatus;
  ai_generated?: boolean;
  reversible?: boolean;
  superseded_step_id?: string | null;
  annotations?: Record<string, any>;
  details?: Record<string, any>;
}

export interface ApplyTransformationRuleRequest {
  recipe_id: string;
  mapping_id: string;
  rule_id: string;
  applied_by?: string;
  step_id?: string;
  target_table?: string;
  target_field?: string;
  inputs?: string[];
  outputs?: string[];
}

export interface UpdateMappingStep {
  step_id?: string;
  operation?: string;
  description?: string;
  inputs?: string[];
  outputs?: string[];
  parameters?: Record<string, any>;
  confidence?: number;
  ai_generated?: boolean;
}

export interface UpdateMappingRequest {
  recipe_id: string;
  source_file?: string | null;
  source_field?: string | null;
  target_table?: string | null;
  target_field?: string | null;
  transformation?: string | null;
  transformation_type?: string;
  operation?: string;
  explanation?: string | null;
  default_value?: any;
  is_required?: boolean;
  confidence?: number;
  step?: UpdateMappingStep;
  remove_transformation?: boolean;
  edited_by?: string;
  /** Position in the hierarchical output structure (e.g. {"SubjectData": "grouping_key"}). */
  hierarchy_path?: Record<string, string> | null;
}

export interface CreateMappingRequest {
  target_table: string;
  target_field: string;
  source_file?: string | null;
  source_field?: string | null;
  transformation?: string | null;
  transformation_type?: string;
  operation?: string;
  parameters?: Record<string, any>;
  created_by?: string;
}

export interface TransformationTestResponse {
  batch_id: string;
  recipe_id: string;
  sample_size: number;
  dependent_sources: string[];
  sample_output: Record<string, Array<Record<string, any>>>;
  warnings: string[];
}

export type OperationType =
  | 'direct'
  | 'expression'
  | 'aggregate'
  | 'window'
  | 'unpivot'
  | 'pivot'
  | 'conditional'
  | 'filter'
  | 'set_constant'
  | 'concat'
  | 'cast';

export interface TransformationStepParsed {
  inputs?: string[];
}

export interface TransformationStep {
  step_id: string;
  kind: string;
  description: string;
  operation?: string;
  expression?: string;
  parameters?: Record<string, unknown>;
  inputs: string[];
  outputs: string[];
  confidence: number;
  ai_generated: boolean;
  parsed?: TransformationStepParsed;
}

export interface MappingRuleTemplateRef {
  template_id: string;
  rule_id?: string;
  name?: string;
  category?: string;
  status?: string;
  version?: number;
  tags?: string[];
  attached_at?: string;
  attached_by?: string;
  rule_source?: string;
}

export type FieldStatus = 'ai_generated' | 'approved' | 'inherited' | 'broken' | 'stale' | 'user_authored';

export interface FieldMapping {
  mapping_id: string;
  source_file: string;
  source_field: string;
  target_table: string;
  target_field: string;
  transformation?: string;
  transformation_type: string;
  operation?: string;
  parameters?: Record<string, unknown>;
  confidence: number;
  is_required: boolean;
  default_value?: unknown;
  explanation?: string;
  transformation_steps?: TransformationStep[];
  rule_templates?: MappingRuleTemplateRef[];
  approved_at?: string | null;
  approved_by?: string | null;
  field_status?: FieldStatus;
  inherited_from_recipe_id?: string | null;
  break_reason?: string | null;
  suggested_resolution?: Record<string, unknown> | null;
  /** Position in the hierarchical output structure (when target has output_structure). */
  hierarchy_path?: Record<string, string> | null;
}

export interface Relationship {
  relationship_id?: string;
  left_table: string;
  right_table: string;
  join_type: string;
  join_keys: Array<Record<string, string>>;
  confidence: number;
  explanation?: string;
}

export interface QualityCheck {
  check_id: string;
  check_name: string;
  check_type: string;
  field: string;
  parameters: Record<string, unknown>;
  threshold: number;
  failure_action: string;
  data: Record<string, unknown>;
}

/* ─── Analytics & Stats ─── */

export interface DashboardStats {
  total_batches: number;
  processed_files: number;
  [key: string]: unknown;
}

/* ─── Semantic Summaries ─── */

/**
 * Semantic summary API may return either a raw array of table summaries
 * or an object wrapping them under a `summaries` key.
 * Consumers should use `Array.isArray(data) ? data : data.summaries` to normalise.
 */
export type SemanticSummaryResponse =
  | Array<Record<string, unknown>>
  | { summaries: Array<Record<string, unknown>>;[key: string]: unknown };

/* ─── Source Preview ─── */

export type SourcePreviewCellValue = string | number | boolean | null;

export interface SourcePreview {
  file_name: string;
  columns: string[];
  rows: SourcePreviewCellValue[][];
  row_count: number;
  total_rows: number;
}

/** Source schema signature used when a full snapshot is not available. */
export interface SourceSchemaSignatureEntry {
  source: string;
  name?: string;
  columns: SchemaColumnSnapshot[];
}

export interface SourceSchemaSignature {
  hash?: string;
  sources?: SourceSchemaSignatureEntry[];
  /** Legacy format: { tableName: [col1, col2, ...] } */
  [key: string]: unknown;
}

/** Alias map that maps canonical table names to display names. */
export interface SchemaAliasMap {
  tables?: Record<string, string>;
}

/** Schema context embedded within mapping metadata. */
export interface MappingSchemaContext {
  target_schema_snapshot?: SchemaSetSnapshot;
  target_schema_set_snapshot?: SchemaSetSnapshot;
}

/** An entry in the aggregation execution plan. */
export interface AggregationPlanStep {
  aggregation_id: string;
  order: number;
  cache_key?: string;
  sheet_id?: string;
  inputs?: string[];
  outputs?: string[];
  [key: string]: unknown;
}

/** Typed metadata for a mapping response. */
export interface MappingMetadata {
  source_schema_set_snapshot?: SchemaSetSnapshot;
  source_schema_signature?: SourceSchemaSignature;
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  source_alias_map?: Record<string, any>;
  target_schema_snapshot?: SchemaSetSnapshot;
  target_schema_set_snapshot?: SchemaSetSnapshot;
  schema_context?: MappingSchemaContext;
  schema_alias_map?: SchemaAliasMap;
  feedback_history?: unknown[];
  rejected_relationships?: Array<Record<string, unknown>>;
  aggregation_plan?: AggregationPlanStep[];
  aggregation_cache?: Record<string, unknown>;
  manual_adjustments?: Array<Record<string, unknown>>;
  semantic_summaries?: Record<string, Record<string, unknown>>;
  /** Schema evolution metadata (populated in delta mode) */
  schema_diff?: Record<string, unknown>;
  file_renames?: Record<string, string>;
  broken_mappings?: FieldMapping[];
  stale_mappings?: FieldMapping[];
  [key: string]: unknown;
}

/**
 * Refinement (Apr 2026): one operator-asserted override of a schema
 * validator rule, written by the `apply_policy(accept_override)`
 * resolve action. Surfaced as a typed first-class field on
 * `MappingResponse` -- not smuggled through `metadata`.
 */
export interface ValidatorOverride {
  rule: string;
  rationale: string;
  accepted_by?: string | null;
  accepted_at?: string | null;
}

/**
 * Refinement (Apr 2026): one recipe-level data-quality marker, written
 * by `apply_policy(drop_rows | fail_load)` resolve actions.
 * pipeline-worker reads the same list at materialization time.
 */
export interface DataQualityAction {
  target_table: string;
  target_field: string;
  policy: 'drop_rows' | 'fail_load' | 'use_sentinel' | 'set_null_with_reason';
  rationale?: string | null;
  operator?: string | null;
  applied_at?: string | null;
}

export interface MappingResponse {
  recipe_id: string;
  batch_id: string;
  client_id: string;
  status: string;
  ai_confidence_score: number;
  created_at: string;
  mappings: FieldMapping[];
  relationships: Relationship[];
  quality_checks: QualityCheck[];
  metadata: MappingMetadata;
  transformation_plan?: TransformationPlan | null;
  dependent_sources: string[];
  sample_output: Record<string, Array<Record<string, unknown>>>;
  suggestions: Suggestion[];
  observations: Observation[];
  project_id?: string | null;
  target_schema_id?: string | null;
  schema_version_hash?: string | null;
  warnings?: HealthIssue[];
  generation_mode?: 'full' | 'delta';
  parent_recipe_id?: string | null;
  /**
   * Refinement: keyed by [target_table][target_field] -> list of rule
   * overrides. Empty `{}` for legacy recipes / when no overrides have
   * been applied.
   */
  validator_overrides: Record<string, Record<string, ValidatorOverride[]>>;
  /**
   * Refinement: recipe-level data-quality markers from
   * `apply_policy(drop_rows | fail_load)` resolves. Empty `[]` for
   * legacy recipes.
   */
  data_quality_actions: DataQualityAction[];
}

// =====================================================================
// Recipe-Batch Decoupling Types
// =====================================================================

export interface ExecutionRun {
  run_id: string;
  recipe_id: string;
  batch_id: string;
  project_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'partial';
  schema_compatibility?: 'compatible' | 'delta_required' | 'incompatible';
  triggered_by?: string;
  started_at?: string | null;
  completed_at?: string | null;
  rows_processed?: number | null;
  rows_failed?: number | null;
  error_message?: string | null;
  table_results: TableResult[];
  metadata?: Record<string, any>;
  created_at?: string;
}

export interface TableResult {
  table: string;
  status: 'success' | 'failed';
  rows: number;
  duration_ms?: number;
  error?: string | null;
}

export interface CompatibilityVerdict {
  compatible: boolean;
  mode: 'direct' | 'delta' | 'full_required';
  source_diff: Record<string, any>;
  target_diff: Record<string, any>;
  source_changes: string;
  target_changes: string;
  reason?: string | null;
}

export interface ApplyRecipeResponse {
  action: 'direct_execution' | 'delta_regeneration' | 'full_required';
  run_id?: string;
  execution_result?: ExecutionRun;
  verdict?: CompatibilityVerdict;
}

export interface ProjectRecipeSummary {
  recipe_id: string;
  batch_id?: string | null;
  status: string;
  mapping_count?: number;
  generation_mode?: string;
  ai_confidence?: number | null;
  parent_recipe_id?: string | null;
  approved_at?: string | null;
  approved_by?: string | null;
  created_at?: string | null;
  execution_count?: number;
  project_id?: string;
  /** Populated when listing cross-project recipes */
  project_name?: string;
  target_schema_id?: string;
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export interface AppNotification {
  notification_id: string;
  user_id: string;
  tenant_id: string;
  project_id: string | null;
  category: 'batch' | 'recipe' | 'execution' | 'validation' | 'schema_drift';
  severity: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  entity_type?: string;
  entity_id?: string;
  metadata: Record<string, any>;
  is_read: boolean;
  created_at: string;
}

export interface NotificationListResponse {
  notifications: AppNotification[];
  unread_count: number;
}

// ---------------------------------------------------------------------------
// Admin / RBAC
// ---------------------------------------------------------------------------

export interface Tenant {
  tenant_id: string;
  target_schema_id?: string;
  name: string;
  slug?: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface TenantCreateRequest {
  name: string;
  slug: string;
  target_schema_id?: string;
}

export interface TenantUpdateRequest {
  name?: string;
  slug?: string;
  is_active?: boolean;
  target_schema_id?: string;
}

export interface UserAdminView {
  user_id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
  tenant_id: string;
  is_active: boolean;
  mfa_enabled: boolean;
  created_at?: string;
  updated_at?: string;
  last_login_at?: string;
}

export interface UserCreateRequest {
  email: string;
  first_name: string;
  last_name?: string;
  role: string;
  tenant_id: string;
  password?: string;
}

export interface UserUpdateRequest {
  first_name?: string;
  last_name?: string;
  password?: string;
  role?: string;
  is_active?: boolean;
}

export interface InviteRequest {
  email: string;
  role: string;
  tenant_id?: string;
}

export interface Invitation {
  token: string;
  email: string;
  role: string;
  tenant_id?: string;
  tenant_name?: string;
  invited_by?: string;
  invited_by_email?: string;
  expires_at: string;
  used_at?: string;
  created_at: string;
}

export interface InviteValidateResponse {
  email: string;
  role: string;
  tenant_id?: string;
  tenant_name?: string;
}

export interface RegisterRequest {
  token: string;
  first_name: string;
  last_name?: string;
  password: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResponse {
  message: string;
}

export interface PasswordResetValidateResponse {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
}

export interface MFAStatusResponse {
  enabled: boolean;
  enforced_for_tenant: boolean;
  pending_setup: boolean;
  backup_codes_remaining: number;
}

export interface MFASetupBeginResponse {
  secret: string;
  otpauth_uri: string;
  qr_image_data_url: string;
}

export interface MFASetupVerifyResponse {
  backup_codes: string[];
}

export interface MFAPolicyResponse {
  enforced: boolean;
}

export interface FeatureFlag {
  id: number;
  tenant_id: string;
  feature_name: string;
  allowed_roles: string;
  is_active: boolean;
  tenant_name?: string;
  created_at?: string;
  updated_at?: string;
}

export interface FeatureFlagCreateRequest {
  tenant_id: string;
  feature_name: string;
  allowed_roles?: string;
  is_active?: boolean;
}

export interface FeatureFlagUpdateRequest {
  feature_name?: string;
  allowed_roles?: string;
  is_active?: boolean;
}

// ---------------------------------------------------------------------------
// LLM Configuration
// ---------------------------------------------------------------------------

export interface LLMConfig {
  config_id: number;
  tenant_id: string | null;
  agent_name: string | null;
  provider_name: string;
  model_name: string;
  is_default: boolean;
  config: Record<string, any> | null;
  created_at?: string;
  updated_at?: string;
}

export interface LLMConfigCreateRequest {
  tenant_id?: string | null;
  agent_name?: string | null;
  provider_name: string;
  model_name: string;
  is_default?: boolean;
  config?: Record<string, any> | null;
}

export interface LLMConfigUpdateRequest {
  provider_name?: string;
  model_name?: string;
  is_default?: boolean;
  config?: Record<string, any> | null;
}

export interface LLMApiKey {
  key_id: number;
  tenant_id: string | null;
  provider_name: string;
  key_hint: string;
  created_at?: string;
  updated_at?: string;
}

export interface LLMApiKeyCreateRequest {
  tenant_id?: string | null;
  provider_name: string;
  api_key: string;
}

export interface ModelPricing {
  pricing_id: number;
  provider_name: string;
  model_name: string;
  input_price_per_mtok: number;
  output_price_per_mtok: number;
  input_price_above_tier: number | null;
  output_price_above_tier: number | null;
  tier_threshold_tokens: number | null;
  context_window: number;
  supports_tools: boolean;
  supports_json_mode: boolean;
  max_output_tokens: number | null;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ModelPricingCreateRequest {
  provider_name: string;
  model_name: string;
  input_price_per_mtok: number;
  output_price_per_mtok: number;
  context_window: number;
  input_price_above_tier?: number | null;
  output_price_above_tier?: number | null;
  tier_threshold_tokens?: number | null;
  supports_tools?: boolean;
  supports_json_mode?: boolean;
  max_output_tokens?: number | null;
  is_active?: boolean;
}

export interface ModelPricingUpdateRequest {
  input_price_per_mtok?: number;
  output_price_per_mtok?: number;
  input_price_above_tier?: number | null;
  output_price_above_tier?: number | null;
  tier_threshold_tokens?: number | null;
  context_window?: number;
  supports_tools?: boolean;
  supports_json_mode?: boolean;
  max_output_tokens?: number | null;
  is_active?: boolean;
}

export interface ProviderInfo {
  provider_name: string;
  models: ModelPricing[];
}

export interface AgentRequirement {
  min_context: number;
  needs_tools: boolean;
  needs_json_mode: boolean;
}

export interface LLMProvidersResponse {
  providers: ProviderInfo[];
  agent_requirements: Record<string, AgentRequirement>;
}

export interface UsageStatsRow {
  usage_id?: number;
  agent_name?: string;
  provider_name?: string;
  model_name?: string;
  batch_id?: string;
  tenant_id?: string;
  tenant_name?: string;
  day?: string;
  created_at?: string;
  earliest_at?: string;
  latest_at?: string;
  total_calls: number;
  total_prompt_tokens: number;
  total_completion_tokens: number;
  total_tokens: number;
  total_cost: number;
}
